import type { StatusTone } from "./tones.js";

/* The high-contrast state ring (ADR-0092 d15). High contrast paints soft tints
   at 16% so coloured text on them stays at 4.5:1 (d13), and at that strength a
   tint no longer shows that an item is selected, current, pressed or the active
   option. So every paint that shows a state with a tint alone adds this ring:
   2px inside the edge, in high contrast only. It is a box shadow, so it follows
   the corners and leaves the focus outline, which sits outside the edge, alone.

   Teal is the selection colour (C7). A state already tinted in its own colour
   (an info facet, a Player AI option, the purple rating) rings in that colour.
   Every colour here is gated at 3:1 on each base surface in high contrast
   (tokens.json $contrast.stateRings). Literal strings so Tailwind sees them. */
export const STATE_RING = {
  teal: "hc:ring-2 hc:ring-inset hc:ring-pb-teal",
  cyan: "hc:ring-2 hc:ring-inset hc:ring-pb-cyan",
  iris: "hc:ring-2 hc:ring-inset hc:ring-pb-iris",
  purple: "hc:ring-2 hc:ring-inset hc:ring-pb-purple",
  green: "hc:ring-2 hc:ring-inset hc:ring-pb-green",
  amber: "hc:ring-2 hc:ring-inset hc:ring-pb-amber",
  red: "hc:ring-2 hc:ring-inset hc:ring-pb-red",
  /* A neutral state: a neutral toned part, whose grey fill shows no colour, or
     the option the arrow keys are on, which is not a selection. Teal stays the
     selection colour, so these ring in the text colour. */
  neutral: "hc:ring-2 hc:ring-inset hc:ring-pb-text",
} as const;

/** The ring of a toned state (a toned pressed Button): its tone's own colour,
 *  and the text colour for the neutral tone, never teal (a toned part paints
 *  its tone, not the selection teal). */
export const STATE_RING_BY_TONE: Readonly<Record<StatusTone, string>> = {
  neutral: STATE_RING.neutral,
  healthy: STATE_RING.green,
  watch: STATE_RING.amber,
  critical: STATE_RING.red,
  info: STATE_RING.cyan,
  ai: STATE_RING.iris,
};
